import { Navigate, useLocation, useParams } from "react-router";
import { playbookFAQLink } from "../../utilities/links";
import { useDocumentTitle } from "../../utilities/useDocumentTitle";
import { PlaybookBody } from "./PlaybookBody";
import { PlaybookBreadcrumb } from "./PlaybookBreadcrumb";
import { PlaybookContentLayout } from "./PlaybookContentLayout";
import { PlaybookDisclaimer } from "./PlaybookDisclaimer";
import { PlaybookGuideCardRow } from "./PlaybookGuideCardRow";
import { usePlaybookFAQ } from "./hooks";
import { FAQBodySkeleton, SkeletonLine } from "./skeletons";

export const PlaybookFAQPage = () => {
  const { shortId } = useParams<{ shortId: string; slug?: string }>();
  const { faq, isLoading } = usePlaybookFAQ(shortId);
  const { pathname } = useLocation();

  useDocumentTitle(faq?.question, "Playbook");

  // TODO: 404 page?
  if (!faq && !isLoading) return <Navigate to="/playbook" replace />;

  // Fix URL if it doesn't match current data
  if (faq && pathname !== playbookFAQLink(faq)) {
    return <Navigate to={playbookFAQLink(faq)} replace />;
  }

  return (
    <div className="mx-auto w-full max-w-4xl pt-16 pb-8xl">
      <PlaybookBreadcrumb
        className="mb-md"
        items={[
          { label: "Playbook", to: "/playbook" },
          faq ? { label: faq.question } : null,
        ]}
      />
      <article className="flex flex-col gap-7xl">
        <div className="flex flex-col gap-md">
          <h1 className="max-w-xl text-display-lg font-semibold tracking-tight text-primary">
            {faq ? faq.question : <SkeletonLine className="w-2/3" />}
          </h1>
        </div>

        <PlaybookContentLayout
          title={faq?.question}
          body={faq && "answer" in faq ? faq.answer : undefined}
        >
          {faq ? (
            <>
              <div>
                <PlaybookBody body={faq.answer} />
              </div>
              <PlaybookDisclaimer className="mt-7xl" />
            </>
          ) : (
            isLoading && <FAQBodySkeleton />
          )}
        </PlaybookContentLayout>

        {faq &&
          "recommended_guides" in faq &&
          faq.recommended_guides.length > 0 && (
            <>
              <hr className="border-secondary" />
              <PlaybookGuideCardRow
                title="Recommended Guides"
                guides={faq.recommended_guides}
              />
            </>
          )}
      </article>
    </div>
  );
};
